import { Flex } from 'components/Flex';
import { useTranslation } from 'hooks/useTranslation';
import {
  FlexWrapper,
  GeneralSettingsContentWrapper,
  ManagedByDropdownWrapper,
  NotificationDescription,
  NotificationLabel,
  SettingsRow,
  SettingsRowLabel,
  SettingsTable,
  SubHeader,
} from './GeneralSettingsPage.styled';
import { SettingsPageWrapper } from 'pages/Settings/SettingsPage.styled';
import { H3, P } from 'components/Typography';
import { FC, MouseEventHandler, useState } from 'react';
import { BKJButton, BKJComboBox, BKJTextInput } from '@bookjane2/bookjane-design-library';
import { BKJCheckbox } from '@bookjane2/bookjane-design-library';
import { useGeneralSettingsFormContext } from './GeneralSettingsPage.form.context';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import isUserEssentialSelector from 'store/selectors/userSelectors/isUserEssentialSelector';
import { useHasMarketplaceSelector } from 'store/selectors/organizationSelectors/orgSettingsSelector';
import { getNotificationPreferenceControlOptions } from './GeneralSettingsPage.constants';
import { SMSNotificationConfirmationDialog } from './SMSNotificationConfirmationDialog';
import { HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE } from 'constants/hardcoded';

export const GeneralSettingsTabEditView: FC = () => {
  const { t } = useTranslation();
  const { values, onChange, isSubmitting, onSubmit } = useGeneralSettingsFormContext();
  const history = useHistory();
  const isUserEssential = useSelector(isUserEssentialSelector);
  const hasMarketplaceAccess = useHasMarketplaceSelector();

  const [isSMSDialogOpen, setIsSMSDialogOpen] = useState(false);
  const [isSMSPricingAlreadyConfirmed, setIsSMSPricingAlreadyConfirmed] = useState(
    !!values?.smsNotificationsEnabled,
  );

  const setSMSEnabled = (enabled: boolean, pricingConfirmed = false) => {
    onChange(
      pricingConfirmed
        ? [
            { target: { name: 'smsNotificationsEnabled', value: enabled } },
            { target: { name: 'smsPricingConfirmed', value: true } },
          ]
        : { target: { name: 'smsNotificationsEnabled', value: enabled } },
    );
  };

  const handleSMSChange = () => {
    const isEnabling = !values?.smsNotificationsEnabled;
    if (!isEnabling) {
      setSMSEnabled(false);
      return;
    }
    if (isSMSPricingAlreadyConfirmed) {
      setSMSEnabled(true);
      return;
    }
    setIsSMSDialogOpen(true);
  };

  return (
    <GeneralSettingsContentWrapper>
      <Flex margin="0 0 40px 0">
        <FlexWrapper height="100%" width="100%">
          <SettingsPageWrapper>
            <Flex flexDirection="column" gap="10px">
              <Flex padding="0 0 30px 0">
                <H3>{t('settings:GENERAL_SETTINGS')}</H3>
              </Flex>
              <Flex padding="0 0 40px 0">
                <P>{t('settings:GENERAL_SETTINGS_DESCRIPTION')}</P>
              </Flex>
              <Flex padding="0 0 15px 0">
                <SubHeader>{t('settings:GENERAL_SETTINGS')}</SubHeader>
              </Flex>
              <SettingsTable flexDirection="column" width="100%">
                <SettingsRow $grey alignItems="center" gap="40px">
                  <SettingsRowLabel flex="1">{t('settings:TIME_FORMAT')}</SettingsRowLabel>
                  <Flex flex="1" justifyContent="flex-end" alignItems="center" gap="16px">
                    <BKJCheckbox
                      variant="GreenSolid"
                      value={values?.is24HourTimeFormat ?? false}
                      name="is24HourTimeFormat"
                      onChange={onChange}
                    />
                    <P>{t('settings:24_HOUR_TIME')}</P>
                  </Flex>
                </SettingsRow>
                {hasMarketplaceAccess && (
                  <SettingsRow alignItems="center" gap="40px">
                    <SettingsRowLabel flex="1">
                      {t('settings:AGENCY_LICENSE_NUMBER')}
                    </SettingsRowLabel>
                    <Flex flex="1" justifyContent="flex-end" alignItems="center" gap="16px">
                      <BKJTextInput
                        name="licenceNumber"
                        placeholder={t('settings:ENTER_AGENCY_LICENSE_NUMBER')}
                        onChange={onChange}
                        value={values?.licenceNumber || ''}
                      />
                    </Flex>
                  </SettingsRow>
                )}
                {!isUserEssential &&
                  HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE && (
                    <SettingsRow
                      $grey={hasMarketplaceAccess}
                      flexDirection="column"
                      alignItems="stretch"
                      gap="24px"
                    >
                      <Flex gap="40px" alignItems="stretch" width="100%">
                        <Flex flexDirection="column" gap="8px" flex="1">
                          <NotificationLabel>
                            {t('settings:ALLOWED_SHIFT_AVAILABILITY_NOTIFICATION_CHANNEL')}
                          </NotificationLabel>
                          <NotificationDescription>
                            {t(
                              'settings:ALLOWED_SHIFT_AVAILABILITY_NOTIFICATION_CHANNEL_DESCRIPTION',
                            )}
                          </NotificationDescription>
                        </Flex>
                        <Flex flexDirection="column" gap="16px" justifyContent="center" flex="1">
                          <Flex gap="24px" alignItems="center">
                            <Flex gap="10px" alignItems="center">
                              <BKJCheckbox
                                key={`sms-checkbox-${isSMSDialogOpen}`}
                                variant="GreenSolid"
                                name="smsNotificationsEnabled"
                                value={values?.smsNotificationsEnabled ?? false}
                                onChange={handleSMSChange}
                              />
                              <P>{t('settings:SMS')}</P>
                            </Flex>
                            <Flex gap="10px" alignItems="center">
                              <BKJCheckbox
                                variant="GreenSolid"
                                name="emailShiftNotificationsEnabled"
                                value={values?.emailShiftNotificationsEnabled ?? false}
                                onChange={onChange}
                              />
                              <P>{t('settings:EMAIL')}</P>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Flex>
                      <Flex gap="40px" alignItems="stretch" width="100%">
                        <Flex flexDirection="column" gap="8px" flex="1">
                          <NotificationLabel>
                            {t('settings:NOTIFICATION_PREFERENCE_CONTROL')}
                          </NotificationLabel>
                          <NotificationDescription>
                            {t('settings:NOTIFICATION_PREFERENCE_CONTROL_DESCRIPTION')}
                          </NotificationDescription>
                        </Flex>
                        <Flex flexDirection="column" gap="16px" justifyContent="center" flex="1">
                          <ManagedByDropdownWrapper>
                            <BKJComboBox
                              name="notificationPreferenceControl"
                              label={t('settings:MANAGED_BY')}
                              value={
                                values?.notificationPreferenceControl
                                  ? [values.notificationPreferenceControl]
                                  : []
                              }
                              onChange={onChange}
                              options={getNotificationPreferenceControlOptions(t)}
                              type="Single"
                              action="OpenToggle"
                              placeholder={t('settings:SELECT')}
                            />
                          </ManagedByDropdownWrapper>
                        </Flex>
                      </Flex>
                    </SettingsRow>
                  )}
              </SettingsTable>
              <Flex padding="10px 0 10px 0" gap="16px">
                <BKJButton
                  type="submit"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  variant="PurpleSolid"
                  width="192px"
                  onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
                >
                  {t('auth:SAVE')}
                </BKJButton>
                <BKJButton
                  variant="PurpleOutlined"
                  width="192px"
                  onClick={() => history.push('/settings/organization/general-settings')}
                >
                  {t('auth:CANCEL')}
                </BKJButton>
              </Flex>
            </Flex>
          </SettingsPageWrapper>
        </FlexWrapper>
      </Flex>
      {!isUserEssential && HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE && (
        <SMSNotificationConfirmationDialog
          isOpen={isSMSDialogOpen}
          onConfirm={() => {
            setSMSEnabled(true, true);
            setIsSMSPricingAlreadyConfirmed(true);
            setIsSMSDialogOpen(false);
          }}
          onCancel={() => setIsSMSDialogOpen(false)}
        />
      )}
    </GeneralSettingsContentWrapper>
  );
};
